/**
 * EmailVerificationModal Component
 *
 * Modal shown when unverified users attempt to upload documents.
 * Provides option to send verification email with clear instructions.
 */

import React, { useState } from 'react';
import { BaseModal } from './BaseModal';
import { Button } from '@/components/ui/button';
import { authService } from '../../services/auth';
import { Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal closes
  const handleClose = () => {
    setSent(false);
    setError('');
    onClose();
  };

  // Handle send verification email
  const handleSendVerification = async () => {
    setSending(true);
    setError('');

    try {
      await authService.sendVerificationEmail();
      setSent(true);
    } catch (err: any) {
      console.error('Failed to send verification email:', err);
      setError(err.message || 'Failed to send verification email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="md"
      title="Email Verification Required"
    >
      <div className="space-y-6">
        {/* Warning icon and message */}
        {!sent ? (
          <>
            <div className="flex items-start gap-4 rounded-xl border border-warning/25 bg-warning/10 p-4">
              <AlertCircle className="mt-0.5 size-6 shrink-0 text-warning" />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">Check Your Inbox</h3>
                <p className="text-sm leading-relaxed text-foreground/70">
                  We've sent a verification email to your inbox. Click the link in the email to
                  verify your account and access all features.
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground/85">What to do:</h4>
              <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
                <li>Check your inbox for an email from Career Catalyst</li>
                <li>Click the verification link in the email</li>
                <li>Return here to start uploading documents</li>
              </ol>
            </div>

            {/* Email tips */}
            <div className="rounded-xl border border-border bg-card/80 p-3 text-sm">
              <p className="text-muted-foreground">
                <strong className="text-foreground/85">Can't find it?</strong> Check your spam
                folder, or click below to resend.
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-destructive/25 bg-destructive/10 p-3">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                className="flex-1"
                onClick={handleSendVerification}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail data-icon="inline-start" />
                    Resend Email
                  </>
                )}
              </Button>
              <Button size="lg" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Success state */}
            <div className="py-4 text-center">
              <div className="mb-6">
                <CheckCircle className="mx-auto size-20 text-success" strokeWidth={1.5} />
              </div>

              <h3 className="mb-3 text-2xl font-bold tracking-tight text-foreground">
                Verification Email Sent!
              </h3>

              <p className="mb-6 text-foreground/70">
                Check your inbox for a verification link. Click the link to verify your email and
                unlock all features.
              </p>

              {/* Email tips */}
              <div className="mb-6 rounded-xl border border-border bg-card/80 p-4 text-left">
                <p className="mb-2 text-sm text-muted-foreground">
                  <strong className="text-foreground">Can't find the email?</strong>
                </p>
                <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                  <li>Check your spam or junk folder</li>
                  <li>Wait a few minutes for delivery</li>
                  <li>Make sure you entered the correct email</li>
                </ul>
              </div>

              <Button size="lg" className="w-full" onClick={handleClose}>
                Got It
              </Button>
            </div>
          </>
        )}

        {/* Footer help text */}
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-foreground/55">
            Need help?{' '}
            <a
              href="mailto:contact@careercatalyst.cc"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </BaseModal>
  );
};
